/* Скачать из Instagram: ссылка на пост, рилс, карусель, сторис или хайлайт —
   либо просто @аккаунт, тогда берём его активные сторис.

   Данные достаёт HikerAPI на сервере. Файлы лежат на CDN Instagram и живут
   несколько часов, поэтому здесь их не храним: выбранное сразу уходит человеку
   в переписку с ботом — оттуда фото и видео сохраняются в галерею одним касанием. */

import { plural } from "@mera/shared";
import { Button, List, ListInput, ListItem } from "konsta/react";
import React, { useState } from "react";

import { Ion, IconTile } from "../components/icons.jsx";
import Screen from "../components/Screen.jsx";
import { Hint, Loader } from "../components/ui.jsx";
import { api, avatarUrl } from "../lib/api.js";
import { useFetch } from "../lib/store.jsx";
import { alertDialog, copyText, haptic } from "../lib/tg.js";

const ITEM_COLORS = {
  primaryTextIos: "text-[color:var(--tg-text)]",
  secondaryTextIos: "text-[color:var(--tg-hint)]",
};

const TILE = { copy: "#30b0c7" };

const KIND = {
  post: "Пост",
  album: "Карусель",
  reel: "Рилс",
  story: "Сторис",
  stories: "Сторис",
  highlight: "Хайлайт",
};

const filesWord = (n) => plural(n, "файл", "файла", "файлов");

/* Длительность ролика — как в плеере: 1:07 */
const clock = (sec) => `${Math.floor(sec / 60)}:${String(sec % 60).padStart(2, "0")}`;

/* Превью: обложка, значок видео с длительностью и галочка выбора */
function Tile({ item, selectable, selected, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="relative block w-full aspect-[9/16] rounded-xl overflow-hidden press"
      style={{ background: "var(--tg-section-bg)" }}
    >
      <img
        src={avatarUrl(item.preview)}
        alt=""
        loading="lazy"
        className="w-full h-full object-cover transition-opacity"
        style={{ opacity: selectable && !selected ? 0.4 : 1 }}
      />

      {item.type === "video" ? (
        <span
          className="absolute left-1.5 bottom-1.5 inline-flex items-center gap-1 rounded-full px-1.5 py-[2px] text-[10px] font-semibold text-white"
          style={{ background: "rgba(0,0,0,.55)" }}
        >
          <Ion.playSolid size={9} />
          {item.duration ? <span className="num">{clock(item.duration)}</span> : null}
        </span>
      ) : null}

      {selectable ? (
        <span className="absolute right-1.5 top-1.5">
          {selected ? (
            <Ion.checkmarkCircleSolid size={20} style={{ color: "var(--tg-button)" }} />
          ) : (
            <Ion.ellipse size={20} style={{ color: "rgba(255,255,255,.75)" }} />
          )}
        </span>
      ) : null}
    </button>
  );
}

export default function Instagram() {
  const { data: status } = useFetch("/instagram/status");
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [picked, setPicked] = useState([]);
  const [sending, setSending] = useState(false);

  const items = data?.items || [];
  const many = items.length > 1;
  const chosen = many ? items.filter((it) => picked.includes(it.id)) : items;

  const toggle = (id) => {
    haptic.select();
    setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  };

  const find = async () => {
    const value = input.trim();
    if (!value || busy) return;
    haptic.light();
    setBusy(true);
    setError(null);
    try {
      const res = await api.post("/instagram/resolve", { input: value });
      setData(res);
      setPicked(res.items.map((it) => it.id));
      haptic.success();
    } catch (e) {
      setData(null);
      setPicked([]);
      setError(e.message);
      haptic.error();
    } finally {
      setBusy(false);
    }
  };

  const send = async () => {
    if (!chosen.length || sending) return;
    haptic.medium();
    setSending(true);
    try {
      const res = await api.post("/instagram/send", {
        items: chosen.map(({ url, type, filename }) => ({ url, type, filename })),
        caption: data.caption || "",
      });
      haptic.success();
      alertDialog(
        res.sent === res.total
          ? `Отправил боту ${res.sent} ${filesWord(res.sent)} — файлы в переписке с ним.`
          : `Отправил ${res.sent} из ${res.total}: остальное Instagram не отдал.`
      );
    } catch (e) {
      haptic.error();
      alertDialog(e.message);
    } finally {
      setSending(false);
    }
  };

  const copy = async () => {
    haptic.light();
    alertDialog((await copyText(data.caption)) ? "Описание скопировано" : data.caption);
  };

  return (
    <Screen back title="Скачать из Instagram" bottomPad={40}>
      <List strong inset>
        <ListInput
          type="text"
          inputMode="url"
          autoCapitalize="off"
          autoCorrect="off"
          spellCheck={false}
          placeholder="Ссылка на пост, рилс, сторис — или @аккаунт"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && find()}
        />
      </List>

      <div className="px-4 pt-3">
        <Button large rounded onClick={find} disabled={busy || !input.trim()}>
          {busy ? "Ищу…" : "Найти"}
        </Button>
      </div>

      {status && !status.ready ? (
        <div className="px-4 pt-3">
          <Hint>Ключ HikerAPI не задан (HIKER_API) — искать нечем.</Hint>
        </div>
      ) : null}

      {status?.ready && !status.chat ? (
        <div className="px-4 pt-3">
          <Hint>Ваш профиль не связан с Telegram — отправить файлы боту не получится.</Hint>
        </div>
      ) : null}

      {busy ? <Loader /> : null}

      {error && !busy ? (
        <div className="px-4 pt-4">
          <div className="card-tg px-4 py-3.5 flex gap-2.5">
            <Ion.warning size={20} style={{ color: "var(--fin-red)" }} />
            <div className="text-[14px] leading-snug">{error}</div>
          </div>
        </div>
      ) : null}

      {data && !busy ? (
        <>
          <div className="px-4 pt-4 pb-1">
            <div className="card-tg px-4 py-3 flex items-center gap-3">
              {data.author?.avatar ? (
                <img
                  src={avatarUrl(data.author.avatar)}
                  alt=""
                  className="w-10 h-10 rounded-full object-cover shrink-0"
                />
              ) : (
                <IconTile icon={Ion.logoInstagram} bg="#c13584" size={40} iconSize={22} />
              )}
              <div className="min-w-0">
                <div className="text-[15px] font-semibold truncate">
                  {data.author?.username ? `@${data.author.username}` : "Instagram"}
                </div>
                <div className="text-[13px] truncate" style={{ color: "var(--tg-hint)" }}>
                  {[KIND[data.kind] || "Запись", `${items.length} ${filesWord(items.length)}`]
                    .filter(Boolean)
                    .join(" · ")}
                </div>
              </div>
            </div>
          </div>

          <div className={`grid gap-2 px-4 pt-3 ${many ? "grid-cols-3" : "grid-cols-1"}`}>
            {items.map((it) => (
              <Tile
                key={it.id}
                item={it}
                selectable={many}
                selected={picked.includes(it.id)}
                onClick={() => (many ? toggle(it.id) : null)}
              />
            ))}
          </div>

          {many ? (
            <div className="px-4 pt-2">
              <Hint>Нажмите на превью, чтобы снять или вернуть выбор.</Hint>
            </div>
          ) : null}

          {data.caption ? (
            <div className="px-4 pt-4">
              <div className="card-tg px-4 py-3.5">
                <div className="text-[14px] leading-snug whitespace-pre-wrap break-words">
                  {data.caption}
                </div>
              </div>
            </div>
          ) : null}

          <div className="px-4 pt-4">
            <Button large rounded onClick={send} disabled={sending || !chosen.length}>
              {sending ? "Отправляю…" : `Отправить в Telegram${many ? ` · ${chosen.length}` : ""}`}
            </Button>
          </div>

          {data.caption ? (
            <List strong inset>
              <ListItem
                link
                chevron={false}
                colors={ITEM_COLORS}
                media={<IconTile icon={Ion.copy} bg={TILE.copy} />}
                title="Скопировать описание"
                onClick={copy}
              />
            </List>
          ) : null}
        </>
      ) : null}

      {!data && !busy && !error ? (
        <div className="px-4 pt-4">
          <Hint>
            Ссылка на пост, карусель, рилс, сторис или хайлайт. Если прислать @аккаунт — скачаются
            его сторис за последние сутки. Закрытые аккаунты недоступны.
          </Hint>
        </div>
      ) : null}
    </Screen>
  );
}
